import React, { useState, useMemo, useEffect } from "react";
import {
  SearchOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  BankOutlined,
  MailOutlined,
  PhoneOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  ClearOutlined,
  ClusterOutlined,
  UserOutlined,
} from "@ant-design/icons";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Select,
  Button,
  Tag,
  Row,
  Col,
  Popconfirm,
  message,
  Pagination,
} from "antd";
import AddProjectModal from "../../components/projects/AddProjectModal";
import useCreateProject from "../../hooks/projects/useCreateProject";
import useEmployeeProjects from "../../hooks/projects/useEmployeeProjects";
import useDeleteProject from "../../hooks/projects/useDeleteProject";
import useOrgHierarchy from "../../hooks/projects/useOrgHierarchy";
import { getCurrentUser } from "../../utils/auth";
import "./Schemes.css";

const { Option } = Select;

const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .toString()
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatStatus = (status) => {
  if (!status) return "N/A";
  return toTitleCase(status);
};

const formatName = (name) => {
  if (!name) return "Untitled Scheme";
  return toTitleCase(name);
};

const formatLocation = (district, taluka, village) => {
  const parts = [district, taluka, village].filter(Boolean).map(toTitleCase);
  return parts.length ? parts.join(", ") : "N/A";
};

function Schemes() {
  const navigate = useNavigate();
  const {
    projects,
    count,
    loading: projectsLoading,
    fetchEmployeeProjects,
  } = useEmployeeProjects();

  // Get hierarchy data (zones + districts)
  const { zonesList = [], districtsList = [] } = useOrgHierarchy();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterZone, setFilterZone] = useState("All");
  const [filterDistrict, setFilterDistrict] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [openAddModal, setOpenAddModal] = useState(false);

  const currentUser = getCurrentUser();
  const organizationId = currentUser?.organization || currentUser?.organization_id;
  const currentUserId = currentUser?.uuid_token || currentUser?.id;

  const { addProject, loading: createLoading } = useCreateProject();
  const { removeProject, loading: deleteLoading } = useDeleteProject();

  // Fetch projects whenever filters change
  useEffect(() => {
    fetchEmployeeProjects({
      zone: filterZone !== "All" ? filterZone : undefined,     // ← Changed to 'zone'
      district: filterDistrict !== "All" ? filterDistrict : undefined,
      project_status: filterStatus !== "All" ? filterStatus : undefined,
      search: searchTerm?.trim() || undefined,
      page: currentPage,
      page_size: pageSize,
    });
  }, [
    filterZone,
    filterDistrict,
    filterStatus,
    searchTerm,
    currentPage,
    pageSize,
    fetchEmployeeProjects,
  ]);

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterZone("All");
    setFilterDistrict("All");
    setFilterStatus("All");
    setCurrentPage(1);
  };

  const handleCreateProject = async (data) => {
    const result = await addProject(data);
    if (result.success) {
      setOpenAddModal(false);
      setCurrentPage(1);
      setTimeout(() => {
        fetchEmployeeProjects({
          zone_name: filterZone !== "All" ? filterZone : undefined,
          district: filterDistrict !== "All" ? filterDistrict : undefined,
          project_status: filterStatus !== "All" ? filterStatus : undefined,
          search: searchTerm?.trim() || undefined,
          page: 1,
          page_size: pageSize,
        });
      }, 300);
    }
    return result;
  };

  const handleDeleteProject = async (projectId) => {
    const result = await removeProject(projectId);
    if (result.success) {
      message.success("Scheme deleted successfully");
      fetchEmployeeProjects({
        zone_name: filterZone !== "All" ? filterZone : undefined,
        district: filterDistrict !== "All" ? filterDistrict : undefined,
        project_status: filterStatus !== "All" ? filterStatus : undefined,
        search: searchTerm?.trim() || undefined,
        page: currentPage,
        page_size: pageSize,
      });
    } else {
      message.error("Failed to delete scheme");
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchTerm(value);
        setCurrentPage(1);
      }, 500),
    []
  );

  // Filter districts based on selected zone
  const filteredDistricts = districtsList.filter(
    (district) => filterZone === "All" || district.zone_name === filterZone
  );

  return (
    <div className="schemes-container">
      <div className="schemes-content">
        <div className="filters-strip">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search Project Name..."
            className="search-input"
            onChange={(e) => debouncedSearch(e.target.value)}
            allowClear
          />

          {/* Zone Filter */}
          <Select
            value={filterZone}
            onChange={(value) => {
              setFilterZone(value);
              setFilterDistrict("All"); // Reset district when zone changes
              setCurrentPage(1);
            }}
            className="filter-select"
            style={{ width: 180 }}
          >
            <Option value="All">All Zones</Option>
            {zonesList.map((zone) => (
              <Option
                key={zone.name || zone.zone_name}
                value={zone.name || zone.zone_name}
              >
                {toTitleCase(zone.name || zone.zone_name)}
              </Option>
            ))}
          </Select>

          {/* District Filter */}
          <Select
            value={filterDistrict}
            onChange={(value) => {
              setFilterDistrict(value);
              setCurrentPage(1);
            }}
            className="filter-select"
            style={{ width: 200 }}
          >
            <Option value="All">All Districts</Option>
            {filteredDistricts.map((district) => (
              <Option
                key={district.district_name}
                value={district.district_name}
              >
                {toTitleCase(district.district_name)}
              </Option>
            ))}
          </Select>

          {/* Status Filter */}
          <Select
            value={filterStatus}
            onChange={(value) => {
              setFilterStatus(value);
              setCurrentPage(1);
            }}
            style={{ width: 160 }}
          >
            <Option value="All">All Status</Option>
            <Option value="IN_PROGRESS">In Progress</Option>
            <Option value="UNIDENTIFIED">Unidentified</Option>
            <Option value="COMPLETED">Completed</Option>
            <Option value="IDENTIFIED">Identified</Option>
          </Select>

          <Button
            icon={<ClearOutlined />}
            className="clear-btn"
            onClick={handleClearFilters}
          >
            Clear
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="add-project-btn"
            onClick={() => setOpenAddModal(true)}
          >
            Add Scheme
          </Button>
        </div>

        <div className="results-count">Showing {count} Schemes</div>

        {projectsLoading ? (
          <div className="loading-container">Loading Schemes...</div>
        ) : (
          <Row gutter={[24, 24]}>
            {projects.map((project) => (
              <Col xs={24} sm={12} lg={8} key={project.id} className="card-equal-height-col">
                <Card
                  className="project-card"
                  cover={
                    <div className="card-image-wrapper">
                      <img
                        alt="project-visual"
                        src={
                          project.image
                            ? project.image
                            : "https://media.istockphoto.com/id/1207400224/photo/high-voltage-power-line-in-a-field-at-sunset.webp?a=1&b=1&s=612x612&w=0&k=20&c=_v_oXSiIkaCeeUw0j6Zic0WOg6f1msfclD6Fu4ZUgp8="
                        }
                        className="card-project-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://media.istockphoto.com/id/1207400224/photo/high-voltage-power-line-in-a-field-at-sunset.webp?a=1&b=1&s=612x612&w=0&k=20&c=_v_oXSiIkaCeeUw0j6Zic0WOg6f1msfclD6Fu4ZUgp8=";
                        }}
                      />
                    </div>
                  }
                >
                  <div className="card-top-line" />

                  <div className="card-body-content">
                    <div className="card-header">
                      <h3 className="project-title">{formatName(project.name)}</h3>
                      <Tag
                        color={
                          project.status === "COMPLETED"
                            ? "purple"
                            : project.status === "IDENTIFIED"
                            ? "green"
                            : project.status === "IN_PROGRESS"
                            ? "blue"
                            : "orange"
                        }
                        className="status-tag"
                      >
                        {formatStatus(project.status)}
                      </Tag>
                    </div>

                    <div className="project-info">
                      <div className="info-row">
                        <BankOutlined />
                        <span>
                          <strong>Zone:</strong> {toTitleCase(project.zone_name) || "N/A"}
                        </span>
                      </div>

                      <div className="info-row">
                        <EnvironmentOutlined />
                        <span>
                          <strong>Location:</strong>{" "}
                          {formatLocation(project.district, project.taluka, project.village)}
                        </span>
                      </div>

                      <div className="info-row">
                        <ClusterOutlined />
                        <span>
                          <strong>Land Type:</strong> {toTitleCase(project.land_type) || "N/A"}
                        </span>
                      </div>

                      <div className="info-row">
                        <CalendarOutlined />
                        <span>
                          <strong>Assigned:</strong> {project.assignment_start_date || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="officer-section">
                      <div className="info-row">
                        <UserOutlined />
                        <span>{toTitleCase(project.assigned_engineer_name) || "N/A"}</span>
                      </div>
                      <div className="info-row">
                        <MailOutlined />
                        <span>{project.sdo_email || "N/A"}</span>
                      </div>
                      <div className="info-row">
                        <PhoneOutlined />
                        <span>{project.sdo_contact || "N/A"}</span>
                      </div>
                    </div>

                    <div className="card-actions">
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/schemes/${project.id}`)}
                      />
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/schemes/${project.id}/edit`)}
                      />
                      <Popconfirm
                        title="Delete Scheme"
                        description="Are you sure you want to delete this scheme?"
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => handleDeleteProject(project.id)}
                      >
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          loading={deleteLoading}
                        />
                      </Popconfirm>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <div className="pagination-wrapper">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={count}
            showSizeChanger
            pageSizeOptions={["9", "10", "20", "50", "100"]}
            onChange={(page, newPageSize) => {
              setCurrentPage(page);
              if (newPageSize !== pageSize) setPageSize(newPageSize);
            }}
            onShowSizeChange={(current, size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} schemes`}
          />
        </div>
      </div>

      <AddProjectModal
        open={openAddModal}
        onCancel={() => setOpenAddModal(false)}
        onSubmit={handleCreateProject}
        loading={createLoading}
        organizationId={organizationId}
        projectAdminId={currentUserId}
      />
    </div>
  );
}

export default Schemes;